import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { V } from '../../../utils/theme';
import { Icons } from '../../../components/Icons';
import { Card, Btn, Field, Chip, Skeleton, SuccessToastCtrl, ConfirmCtrl } from '../../../components/ui';
import { Notice, Eyebrow, Callout } from '../../../components/kit';
import { fmtDay } from '../../../utils/org';
import { useApi, StatusPill, RowBtn, SmallLabel } from './shared';

// ─── Invites tab ───
// list_invites {org_id} (rows carry `id`, `url`, `effective_status`) · create_invite · revoke_invite {org_id, id}.
// The join URL gets a copy button and an on-device QR (the `qrcode` package, same as the friend code).
const invitePill = (s) => (s === 'active' ? 'active' : s === 'revoked' ? 'closed' : 'suspended');

function InviteQr({ url }) {
  const [svg, setSvg] = useState('');
  useEffect(() => {
    let alive = true;
    if (!url) { setSvg(''); return; }
    QRCode.toString(url, { type: 'svg', margin: 0, errorCorrectionLevel: 'M', color: { dark: '#0e0e16', light: '#ffffff' } }).then(s => { if (alive) setSvg(s); }).catch(() => {});
    return () => { alive = false; };
  }, [url]);
  if (!svg) return null;
  return <div data-testid="invite-qr" aria-label="Invite QR code" role="img" style={{ width: 132, height: 132, background: "#fff", borderRadius: 12, paddingTop: 8, paddingBottom: 8, paddingLeft: 8, paddingRight: 8, boxSizing: "border-box", flexShrink: 0 }} dangerouslySetInnerHTML={{ __html: svg }} />;
}

export function InvitesTab({ orgId, coaches }) {
  const api = useApi();
  const [invites, setInvites] = useState(null);
  const [err, setErr] = useState('');
  const [role, setRole] = useState('member');
  const [coach, setCoach] = useState('');
  const [email, setEmail] = useState('');
  const [maxUses, setMaxUses] = useState('1');
  const [days, setDays] = useState('14');
  const [created, setCreated] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const load = async () => {
    const r = await api('list_invites', { org_id: orgId });
    if (r.ok) { setInvites(r.data.invites || []); setErr(''); } else { setInvites(i => i || []); setErr(r.msg); }
  };
  useEffect(() => { setInvites(null); load(); }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy(true); setMsg(null); setCreated(null);
    const r = await api('create_invite', { org_id: orgId, role, ...(role === 'member' && coach ? { coach_email: coach } : {}), ...(email.trim() ? { email: email.trim() } : {}), max_uses: parseInt(maxUses) || 1, expires_days: parseInt(days) || 14 });
    if (r.ok) { setCreated(r.data.invite); load(); } else setMsg(r.msg);
    setBusy(false);
  };
  const copy = (url) => { navigator.clipboard?.writeText(url).then(() => SuccessToastCtrl.show('Link copied')).catch(() => {}); };
  const revoke = (i) => ConfirmCtrl.show(`Revoke invite ${i.code}?`, 'Anyone holding this link can no longer join. This cannot be undone.', async () => {
    setRevoking(i.id); setErr('');
    const r = await api('revoke_invite', { org_id: orgId, id: i.id });
    if (r.ok) { SuccessToastCtrl.show('Invite revoked'); await load(); } else setErr(r.msg);
    setRevoking(null);
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="org-invites">
      <Card>
        <Eyebrow style={{ marginBottom: 10 }}>Create invite</Eyebrow>
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>{['member', 'coach'].map(r => <Chip key={r} label={r} active={role === r} onClick={() => setRole(r)} />)}</div>
        {role === 'member' && coaches.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <SmallLabel style={{ marginBottom: 6 }}>Assign to coach (optional)</SmallLabel>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <Chip label="None" active={!coach} onClick={() => setCoach('')} />
              {coaches.map(c => <Chip key={c.user_email} label={c.name || c.user_email} active={coach === c.user_email} onClick={() => setCoach(c.user_email)} />)}
            </div>
          </div>
        )}
        <Field label="Lock to email (optional)" value={email} onChange={setEmail} type="email" placeholder="member@example.com" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <Field label="Max uses" value={maxUses} onChange={setMaxUses} type="number" min={1} max={500} />
          <Field label="Expires in (days)" value={days} onChange={setDays} type="number" min={1} max={90} />
        </div>
        {msg && <Notice tone="error" style={{ marginBottom: 8 }}>{msg}</Notice>}
        <Btn full disabled={busy} onClick={create}>{busy ? 'Creating…' : 'Create invite'}</Btn>
        {created && (
          <Callout icon={Icons.check} style={{ marginTop: 10 }} title={`Invite ${created.code}`}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
              <InviteQr url={created.url} />
              <div style={{ flex: 1, minWidth: 160 }}>
                <div data-testid="invite-url" style={{ fontFamily: V.mono, fontSize: 11, wordBreak: "break-all" }}>{created.url}</div>
                <div style={{ fontSize: 10, color: V.text3, marginTop: 4 }}>{created.role} · {created.max_uses} use{created.max_uses === 1 ? '' : 's'} · expires {fmtDay(created.expires_at)}</div>
                <Btn v="small" onClick={() => copy(created.url)} s={{ marginTop: 6 }}>{Icons.copy({ size: 13, color: V.accent })} Copy link</Btn>
              </div>
            </div>
          </Callout>
        )}
      </Card>
      {err && <Notice tone="error">{err}</Notice>}
      <Card>
        <Eyebrow style={{ marginBottom: 8 }}>Invites {invites ? `(${invites.length})` : ''}</Eyebrow>
        {invites === null ? <Skeleton rows={3} /> : invites.length === 0 ? <div style={{ fontSize: 12, color: V.text3 }}>No invites yet.</div> : invites.map(i => {
          const st = i.effective_status || i.status;
          return (
            <div key={i.id || i.code} data-testid="invite-row" data-code={i.code} style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 6, paddingBottom: 6, borderTop: `1px solid ${V.divider}`, opacity: revoking === i.id ? .6 : 1, flexWrap: "wrap" }}>
              <span style={{ fontFamily: V.mono, fontSize: 12, fontWeight: 700, color: V.text }}>{i.code}</span>
              <span style={{ fontSize: 11, color: V.text3, flex: 1, minWidth: 120 }}>{i.role}{i.coach_email ? ` → ${i.coach_email}` : ''}{i.email ? ` · ${i.email}` : ''} · {i.uses}/{i.max_uses} · exp {fmtDay(i.expires_at)}</span>
              <StatusPill status={invitePill(st)} />
              {st === 'active' && i.url && <RowBtn onClick={() => copy(i.url)} ariaLabel={`Copy invite ${i.code}`}>Copy</RowBtn>}
              {st === 'active' && i.id && <RowBtn onClick={() => revoke(i)} disabled={revoking !== null} color={V.danger} ariaLabel={`Revoke invite ${i.code}`}>Revoke</RowBtn>}
            </div>
          );
        })}
      </Card>
    </div>
  );
}
